import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Observable, Subscription, of, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { SseClientService, SseEvent, SseStreamError } from "../../core/sse";
import { SovUploadSummary } from "../../model/maia/sov-upload";
import {
    AttentionColumn,
    AttentionRow,
    GeocodeApiResponse,
    GeocodeEvent,
    GeocodeFailedLocation,
    GeocodeJobEvent,
    GeocodeNotFoundLocation,
    GeocodeStatus,
    ParsedCoordinates,
    GeocodeSummary,
} from "../../model/maia/sov-geocode";

/**
 * MAIA SOV geocode — Step 2.
 *
 * Confirmed against the real backend:
 *
 * 1. `POST /sov/{jobId}/geocode` only ever acknowledges the job started
 *    (`{jobId, stageStatus: "STARTED", message}`) — it is never itself a
 *    terminal response. Every call opens a **fresh** `GET /sov/{jobId}/events`
 *    connection right after; the connection used to watch upload's parsing
 *    has already closed and cannot be reused for this stage (true for every
 *    stage going forward — one connection per stage trigger).
 * 2. The first progress frame is special: `{jobId, status: "GEOCODING_IN_PROGRESS", totalRows}` —
 *    no message, but it gives the job's total row count. Every frame after
 *    that carries only `{jobId, message}`, a free-text string like
 *    "Processed batch of 500 row(s) (450 geocoded: 400 rooftop, 30
 *    approximation, 20 not found)". Only the batch size ("500") is read out
 *    of it, as the progress bar's numerator against the first frame's
 *    totalRows denominator — a cosmetic number, so its dependence on that
 *    exact wording is tolerable.
 * 3. The stream carries NO reliable counts. Preserved/Geocoded/Failed come
 *    from GET /sov/upload/{jobId}/summary, which the server maintains with
 *    atomic increments per batch. They used to be scraped out of the message
 *    above, which read as zero whenever the sentence was absent or reworded —
 *    and silently reported a job with 31 failures as fully resolved.
 * 4. No frame — live or snapshot — carries individual row/address detail,
 *    only aggregate counts. So `GeocodeSummary.failedCount` (a number) is
 *    real and drives the needs-attention screen correctly, but
 *    `GeocodeSummary.failed` (the itemized, addressable list) stays empty
 *    until the backend adds that.
 *
 * Unlike upload, this stream's own status vocabulary
 * (`GEOCODING_COMPLETE`/`GEOCODING_FAILED`) differs from upload's
 * (`PARSED`/`FAILED`), so this talks to `SseClientService` directly with its
 * own terminal predicates rather than going through `MaiaUploadService`.
 *
 */
@Injectable({
    providedIn: "root",
})
export class MaiaGeocodeService {
    private static readonly JOB_EVENTS_URL = (jobId: number) => `/sov/${jobId}/events`;
    private static readonly BATCH_SIZE_PATTERN = /processed batch of (\d+) row/i;

    /**
     * How many rows the attention table lets the user fix inline. Beyond this
     * the list stops being a practical editing surface, and "Download to
     * Review or Edit" becomes the route instead.
     */
    static readonly MAX_EDITABLE_ROWS = 20;

    /**
     * Column key for the coordinate pair. It is NOT a `rawData` key — the API
     * returns lat/lng/latLng beside `rawData` — so this column is appended to
     * the discovered set rather than found in it.
     */
    static readonly COORDINATE_KEY = "latLng";

    /**
     * Keys that exist in `rawData` but get no column of their own. `lat`/`lng`
     * are informational — the editable pair is `latLng`, and showing all three
     * would give the user two ways to say the same thing.
     */
    private static readonly INFO_ONLY_KEYS = ["lat", "lng"];

    /**
     * Ordering for a Google Maps query, most specific first.
     *
     * The sheet's own column order is whatever the customer happened to use —
     * in the observed data it is Country, City, Zip, Address, i.e. exactly
     * backwards. Handing Maps "Uruguay, Saucedo, 50010, Rigoberto Lopez 8833"
     * fails; "Rigoberto Lopez 8833, Saucedo, 50010, Uruguay" resolves. So the
     * parts are reordered by what the column means, not where it sat.
     *
     * Anything unrecognised ranks just after the street line, since custom
     * columns are usually address detail (unit, building, block).
     */
    private static readonly QUERY_PART_ORDER: Array<{ pattern: RegExp; rank: number }> = [
        { pattern: /(address|street|addr|line|road)/i, rank: 0 },
        { pattern: /(city|town|locality|suburb|village)/i, rank: 2 },
        { pattern: /(state|province|region|county|district)/i, rank: 3 },
        { pattern: /(zip|postal|postcode)/i, rank: 4 },
        { pattern: /country/i, rank: 5 },
    ];

    private static readonly UNKNOWN_PART_RANK = 1;

    /**
     * Display order for the attention table: the standard address order the
     * client asked for - Address, City, State, Zip, Country - then Coordinates.
     *
     * Deliberately not QUERY_PART_ORDER above, even though the two now agree.
     * That one exists to make Google Maps resolve a query; this one is what
     * the client asked to read on screen (it previously put Country before
     * Zip). Tying them together would mean a change to either silently moving
     * the other.
     *
     * Also not the sheet's own column order, which is whatever the customer
     * happened to use — in the observed data Country, City, Zip, Address, so
     * the field people actually retype first sat fourth.
     */
    private static readonly COLUMN_DISPLAY_ORDER: Array<{ pattern: RegExp; rank: number }> = [
        { pattern: /(address|street|addr|line|road)/i, rank: 0 },
        { pattern: /(city|town|locality|suburb|village)/i, rank: 1 },
        // Not in the client's list, since their sheets carry no State column.
        // Placed on the city/country hierarchy so it lands somewhere sensible
        // when a sheet does have one, without disturbing the asked-for order.
        { pattern: /(state|province|region|county|district)/i, rank: 2 },
        { pattern: /(zip|postal|postcode)/i, rank: 3 },
        { pattern: /country/i, rank: 4 },
    ];

    /** Custom columns sit after the address fields the user came here to fix. */
    private static readonly UNKNOWN_COLUMN_RANK = 5;

    /** Coordinates is always last: it is the one field being added, not corrected. */
    private static readonly COORDINATE_COLUMN_RANK = 6;

    /** Labels that shouldn't just be the humanised key. */
    private static readonly COLUMN_LABELS: { [key: string]: string } = {
        latLng: "Coordinates",
    };

    constructor(
        private readonly http: HttpClient,
        private readonly sse: SseClientService
    ) {}

    /**
     * Starts the geocode job for `jobId`, then follows its progress over a
     * fresh SSE connection until a terminal frame arrives.
     */
    startGeocode(jobId: number, totalLocations: number): Observable<GeocodeEvent> {
        return new Observable<GeocodeEvent>((subscriber) => {
            subscriber.next({ status: GeocodeStatus.IN_PROGRESS, message: "Starting…", percent: 0 });

            let streamSubscription: Subscription | undefined;
            // Per-call state (not instance fields — this service is a singleton
            // and shared fields would leak between concurrent jobs).
            let totalRowsTarget = totalLocations;
            let processedRows = 0;

            const kickoff = this.http.post<GeocodeApiResponse>(`/sov/${jobId}/geocode`, {}).subscribe({
                next: () => {
                    // The kickoff response is only ever an ack — always open a new stream after it.
                    streamSubscription = this.streamGeocodeEvents(jobId).subscribe({
                        next: (event) => {
                            const status = this.terminalStatusOf(event);

                            if (status === GeocodeStatus.COMPLETE) {
                                // No counts here on purpose: the caller reads
                                // them from GET /sov/upload/{jobId}/summary,
                                // which the server maintains per batch. They
                                // used to be scraped out of the progress text,
                                // which silently read as zero whenever that
                                // sentence was missing or reworded.
                                subscriber.next({ status: GeocodeStatus.COMPLETE });
                                return;
                            }

                            if (status === GeocodeStatus.FAILED) {
                                const payload = event.data;
                                subscriber.next({
                                    status: GeocodeStatus.FAILED,
                                    error: (payload && payload.error) || "Something went wrong while geocoding. Please try again.",
                                });
                                return;
                            }

                            const payload = event.data;
                            if (payload && typeof payload.totalRows === "number") {
                                totalRowsTarget = payload.totalRows;
                            }

                            const message = payload && payload.message;
                            const batchSize = this.extractBatchSize(message);
                            if (batchSize !== null) {
                                processedRows += batchSize;
                            }

                            subscriber.next({
                                status: GeocodeStatus.IN_PROGRESS,
                                percent: this.computeProgressPercent(processedRows, totalRowsTarget),
                                message: message || event.raw || "Resolving locations…",
                            });
                        },
                        error: (error) => {
                            subscriber.next({
                                status: GeocodeStatus.FAILED,
                                error: this.extractStreamErrorMessage(error),
                            });
                            subscriber.complete();
                        },
                        complete: () => {
                            // The terminal COMPLETE event was already emitted above.
                            subscriber.complete();
                        },
                    });
                },
                error: (error) => {
                    subscriber.next({
                        status: GeocodeStatus.FAILED,
                        error: this.extractErrorMessage(error),
                    });
                    subscriber.complete();
                },
            });

            return () => {
                kickoff.unsubscribe();
                if (streamSubscription) {
                    streamSubscription.unsubscribe();
                }
            };
        });
    }

    /**
     * The locations that need manual attention after a geocode job completes.
     *
     * Call this once the geocode stream reaches GEOCODING_COMPLETE — the SSE
     * frames only ever carry aggregate counts, so this is the only source of
     * per-row detail.
     *
     * Returns `[]` rather than erroring on a 404, so a backend that omits the
     * endpoint for a fully-resolved job just yields the "all resolved" screen.
     */
    getNotFoundLocations(jobId: number): Observable<GeocodeNotFoundLocation[]> {
        return this.http
            .get<GeocodeNotFoundLocation[]>(`/sov/${jobId}/geocode/not-found`)
            .pipe(
                map((locations) => locations || []),
                catchError((error) => {
                    if (error instanceof HttpErrorResponse && error.status === 404) {
                        return of([] as GeocodeNotFoundLocation[]);
                    }
                    return throwError(error);
                })
            );
    }

    /**
     * Downloads the not-found rows as a workbook for offline editing.
     *
     * `observe: "response"` so the caller can read the filename out of
     * Content-Disposition; the body is a Blob.
     */
    exportNotFoundLocations(jobId: number): Observable<HttpResponse<Blob>> {
        return this.http.get(`/sov/${jobId}/geocode/not-found/export`, {
            observe: "response",
            responseType: "blob",
        });
    }

    /**
     * The job's geocoding counters.
     *
     * Server-maintained (atomic $inc per batch), so these are the authoritative
     * numbers behind Preserved / Geocoded / Failed. Previously these were
     * inferred from the stream's free-text progress messages, which read as
     * zero whenever that sentence was missing or reworded.
     */
    getJobSummary(jobId: number): Observable<SovUploadSummary> {
        return this.http.get<SovUploadSummary>(`/sov/upload/${jobId}/summary`);
    }

    /**
     * Preserved / Geocoded / Failed exactly as the screen labels them.
     *
     *   Preserved = geocodingSkippedHadCoordinates
     *   Geocoded  = geocodedRooftop + geocodedApproximation
     *   Failed    = geocodedNotFound + geocodingErrors
     */
    buildSummaryFromCounters(summary: SovUploadSummary, totalLocations: number): GeocodeSummary {
        const preserved = summary.geocodingSkippedHadCoordinates || 0;
        const rooftop = summary.geocodedRooftop || 0;
        const approximation = summary.geocodedApproximation || 0;
        const geocoded = rooftop + approximation;
        // A geocoding error is a failure from the user's side — the location
        // has no coordinates either way — so it is counted with not-found.
        const failedCount = (summary.geocodedNotFound || 0) + (summary.geocodingErrors || 0);
        const total = summary.totalRecords || totalLocations || 0;

        return {
            totalLocations: total,
            preserved,
            geocoded,
            rooftopCount: rooftop,
            approximationCount: approximation,
            failedCount,
            failed: [],
        };
    }

    /**
     * Reads the Coordinates cell into a lat/lng pair.
     *
     * The column is a single text box because that is what people paste out of
     * Google Maps ("61.65, 15.15"), but the API wants the two numbers apart —
     * so the pair is split here, client-side, before the request.
     *
     * An empty cell is not an error: it just means the row still has no
     * coordinates. Separators may be a comma, whitespace, or both.
     */
    parseCoordinates(value: string): ParsedCoordinates {
        const trimmed = (value || "").trim();
        if (!trimmed) {
            return { coordinates: null, error: null };
        }

        const parts = trimmed.split(/[,\s]+/).filter((part) => part.length > 0);
        if (parts.length !== 2) {
            return {
                coordinates: null,
                error: 'Enter coordinates as "latitude, longitude" — e.g. 61.65, 15.15',
            };
        }

        const lat = Number(parts[0]);
        const lng = Number(parts[1]);

        if (!isFinite(lat) || !isFinite(lng)) {
            return {
                coordinates: null,
                error: 'Enter coordinates as "latitude, longitude" — e.g. 61.65, 15.15',
            };
        }

        // Ranges are worth checking: latitude and longitude transposed is the
        // single most common paste error, and it silently maps to the wrong
        // place whenever both values happen to be under 90.
        if (lat < -90 || lat > 90) {
            return { coordinates: null, error: "Latitude must be between -90 and 90." };
        }
        if (lng < -180 || lng > 180) {
            return { coordinates: null, error: "Longitude must be between -180 and 180." };
        }

        return { coordinates: { lat, lng }, error: null };
    }

    /**
     * Rebuilds the API's own row objects with the user's edits applied.
     *
     * The server wants the whole row back — id, sheetOfOrigin, lat/lng and any
     * rawData keys the table never rendered — so each payload entry starts as a
     * copy of the original and only the edited fields are overwritten.
     */
    buildUpdatePayload(
        columns: AttentionColumn[],
        rows: AttentionRow[]
    ): GeocodeNotFoundLocation[] {
        return rows.map((row) => {
            const rawData = { ...(row.source.rawData || {}) };

            for (const column of columns) {
                if (column.key === MaiaGeocodeService.COORDINATE_KEY) {
                    continue;
                }
                rawData[column.key] = row.values[column.key] || "";
            }

            // The user types a pair into one box; the API wants it split.
            // Callers validate first, so an unparseable value here would be a
            // bug — fall back to the row's original pair rather than send junk.
            const typed = (row.values[MaiaGeocodeService.COORDINATE_KEY] || "").trim();
            const parsed = this.parseCoordinates(typed);

            if (!typed) {
                return { ...row.source, rawData, lat: null, lng: null, latLng: null };
            }

            if (!parsed.coordinates) {
                return { ...row.source, rawData };
            }

            return {
                ...row.source,
                rawData,
                lat: parsed.coordinates.lat,
                lng: parsed.coordinates.lng,
                latLng: `${parsed.coordinates.lat}, ${parsed.coordinates.lng}`,
            };
        });
    }

    /**
     * Persists edited rows — the whole row object per entry, not a diff.
     *
     * PATCH because a call may carry a single row (the per-row Update) or the
     * complete list (Update now); either way it is a partial update of the
     * job's not-found set.
     */
    updateNotFoundLocations(
        jobId: number,
        payload: GeocodeNotFoundLocation[]
    ): Observable<any> {
        return this.http.patch(`/sov/${jobId}/geocode/not-found`, payload);
    }

    /**
     * Sends the corrected workbook back after the user edited it offline.
     *
     * Path mirrors the export (`/sov/{jobId}/geocode/not-found/…`); the field
     * name matches the SOV upload's `file`.
     */
    importCorrectedLocations(jobId: number, file: File): Observable<any> {
        const formData = new FormData();
        formData.append("file", file);

        return this.http.post(`/sov/${jobId}/geocode/not-found/import`, formData);
    }

    /**
     * Filename for the exported workbook.
     *
     * Prefers the server's Content-Disposition. That header is only readable
     * cross-origin when the server sends
     * `Access-Control-Expose-Headers: Content-Disposition`, which it may not —
     * hence the fallback rather than a crash or a file called "download".
     */
    resolveExportFileName(response: HttpResponse<Blob>, jobId: number): string {
        const disposition = response.headers.get("Content-Disposition") || "";

        // RFC 5987 form first: filename*=UTF-8''name.xlsx
        const encoded = disposition.match(/filename\*=(?:UTF-8'')?"?([^";]+)"?/i);
        if (encoded && encoded[1]) {
            try {
                return decodeURIComponent(encoded[1]);
            } catch {
                return encoded[1];
            }
        }

        const plain = disposition.match(/filename="?([^";]+)"?/i);
        if (plain && plain[1]) {
            return plain[1].trim();
        }

        return `geocode-not-found-${jobId}.xlsx`;
    }

    /**
     * Builds the attention table's columns from the rows themselves.
     *
     * The API sends no column metadata, so the column set is the union of every
     * row's `rawData` keys — a union, not the first row's keys, because rows
     * from different sheets can carry different fields.
     *
     * Ordered by what each column means (COLUMN_DISPLAY_ORDER), not by where it
     * sat in the sheet. Columns that match nothing keep their first-seen order
     * relative to each other, so a workbook's own custom fields still read in
     * the order it listed them.
     */
    buildAttentionColumns(locations: GeocodeNotFoundLocation[]): AttentionColumn[] {
        const seen: string[] = [];

        for (const location of locations || []) {
            const raw = location && location.rawData;
            if (!raw) {
                continue;
            }
            for (const key of Object.keys(raw)) {
                if (MaiaGeocodeService.INFO_ONLY_KEYS.indexOf(key) !== -1) {
                    continue;
                }
                if (seen.indexOf(key) === -1) {
                    seen.push(key);
                }
            }
        }

        const columns = seen.map((key) => ({
            key,
            label: MaiaGeocodeService.COLUMN_LABELS[key] || this.humaniseKey(key),
        }));

        // Coordinates always gets a column, even though it never appears in
        // rawData — it is the field the user is most often here to fill in.
        // Guarded in case the backend ever moves it into rawData.
        if (!columns.some((column) => column.key === MaiaGeocodeService.COORDINATE_KEY)) {
            columns.push({
                key: MaiaGeocodeService.COORDINATE_KEY,
                label: MaiaGeocodeService.COLUMN_LABELS[MaiaGeocodeService.COORDINATE_KEY],
            });
        }

        // Decorated sort: Array#sort is only guaranteed stable from ES2019, and
        // the first-seen order of unrecognised columns is worth keeping.
        return columns
            .map((column, index) => ({ column, index, rank: this.columnRank(column.key) }))
            .sort((a, b) => a.rank - b.rank || a.index - b.index)
            .map((entry) => entry.column);
    }

    /** Where a column sits on screen — see COLUMN_DISPLAY_ORDER. */
    private columnRank(key: string): number {
        if (key === MaiaGeocodeService.COORDINATE_KEY) {
            return MaiaGeocodeService.COORDINATE_COLUMN_RANK;
        }
        for (const entry of MaiaGeocodeService.COLUMN_DISPLAY_ORDER) {
            if (entry.pattern.test(key)) {
                return entry.rank;
            }
        }
        return MaiaGeocodeService.UNKNOWN_COLUMN_RANK;
    }

    /** Lifts the API rows into the editable rows the table binds to. */
    buildAttentionRows(
        locations: GeocodeNotFoundLocation[],
        columns: AttentionColumn[]
    ): AttentionRow[] {
        return (locations || []).map((location) => {
            const values: { [key: string]: string } = {};

            for (const column of columns) {
                values[column.key] = this.toEditableValue(this.readField(location, column.key));
            }

            const lat = this.toEditableValue(location.lat);
            const lng = this.toEditableValue(location.lng);

            return {
                // rowIndex is only unique within a sheet — see AttentionRow.id.
                id: `${location.sheetOfOrigin}:${location.rowIndex}`,
                // lat/lng get no column, so they ride along as a hint on the
                // Coordinates input rather than being dropped entirely.
                coordinateHint: lat && lng ? `${lat}, ${lng}` : null,
                source: location,
                rowIndex: location.rowIndex,
                sheetOfOrigin: location.sheetOfOrigin,
                values,
                originalValues: { ...values },
                isSaving: false,
                error: null,
            };
        });
    }

    /**
     * Builds the Google Maps search string for a row.
     *
     * Prefers the coordinate pair when the user has one — that always
     * resolves. Otherwise the address parts are ordered specific-to-general
     * (see QUERY_PART_ORDER) rather than in sheet-column order.
     */
    buildMapQuery(columns: AttentionColumn[], row: AttentionRow): string {
        const coordinates = (row.values[MaiaGeocodeService.COORDINATE_KEY] || "").trim();
        if (coordinates) {
            return coordinates;
        }

        return columns
            .filter((column) => column.key !== MaiaGeocodeService.COORDINATE_KEY)
            .map((column) => ({
                rank: this.queryPartRank(column.key),
                value: (row.values[column.key] || "").trim(),
            }))
            .filter((part) => !!part.value)
            .sort((a, b) => a.rank - b.rank)
            .map((part) => part.value)
            .join(", ");
    }

    private queryPartRank(key: string): number {
        for (const entry of MaiaGeocodeService.QUERY_PART_ORDER) {
            if (entry.pattern.test(key)) {
                return entry.rank;
            }
        }
        return MaiaGeocodeService.UNKNOWN_PART_RANK;
    }

    /**
     * Reads a column's value. Coordinates live at the top level of the
     * response; every other column comes from `rawData`.
     */
    private readField(location: GeocodeNotFoundLocation, key: string): unknown {
        if (key === MaiaGeocodeService.COORDINATE_KEY) {
            return location.latLng;
        }
        return location.rawData ? location.rawData[key] : null;
    }

    /**
     * `rawData` values are whatever the sheet held — numbers, booleans, nulls.
     * The inputs are text, so everything becomes a string, and null/undefined
     * becomes "" so the box renders blank rather than showing "null".
     */
    private toEditableValue(value: unknown): string {
        if (value === null || value === undefined) {
            return "";
        }
        if (typeof value === "object") {
            return JSON.stringify(value);
        }
        return String(value);
    }

    /** "zip_code" / "zipCode" -> "Zip Code". The API sends no labels. */
    private humaniseKey(key: string): string {
        return key
            .replace(/[_-]+/g, " ")
            .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
            .trim()
            .replace(/\s+/g, " ")
            .replace(/\b\w/g, (character) => character.toUpperCase());
    }

    private streamGeocodeEvents(jobId: number): Observable<SseEvent<GeocodeJobEvent>> {
        return this.sse.streamUntil<GeocodeJobEvent>(MaiaGeocodeService.JOB_EVENTS_URL(jobId), {
            maxRetries: 2,
            isComplete: (event) => this.terminalStatusOf(event) === GeocodeStatus.COMPLETE,
            isFailure: (event) => this.terminalStatusOf(event) === GeocodeStatus.FAILED,
        });
    }

    /** Status of a frame — from the payload, falling back to the SSE `event:` name for progress frames that carry no status at all. */
    private terminalStatusOf(event: SseEvent<GeocodeJobEvent>): GeocodeStatus | null {
        if (event.data && event.data.status) {
            return event.data.status;
        }
        switch (event.event) {
            case "done":
                return GeocodeStatus.COMPLETE;
            case "error":
                return GeocodeStatus.FAILED;
            default:
                return null;
        }
    }

    /** Pulls the row count out of "Processed batch of 500 row(s)..." — see class doc point 2. */
    private extractBatchSize(message: string | null | undefined): number | null {
        if (!message) {
            return null;
        }
        const match = message.match(MaiaGeocodeService.BATCH_SIZE_PATTERN);
        return match ? parseInt(match[1], 10) : null;
    }

    /** Capped below 100 while still IN_PROGRESS, so batch-size rounding can't show "done" before the terminal frame actually arrives. */
    private computeProgressPercent(processedRows: number, totalRows: number): number {
        if (!totalRows) {
            return 0;
        }
        const percent = Math.round((processedRows / totalRows) * 100);
        return Math.max(0, Math.min(99, percent));
    }

    private extractErrorMessage(error: unknown): string {
        const fallback = "Something went wrong while geocoding. Please try again.";

        if (error instanceof HttpErrorResponse) {
            if (error.status === 0) {
                return "Could not reach the server. Check your connection and try again.";
            }
            const body = error.error;
            if (typeof body === "string" && body.trim()) {
                return body;
            }
            if (body && typeof body.message === "string" && body.message.trim()) {
                return body.message;
            }
            if (body && typeof body.error === "string" && body.error.trim()) {
                return body.error;
            }
            return error.message || fallback;
        }

        return fallback;
    }

    private extractStreamErrorMessage(error: unknown): string {
        const fallback = "We couldn't finish geocoding. Please check your data and try again.";

        if (error instanceof SseStreamError) {
            const payload = (error.event as SseEvent<GeocodeJobEvent>).data;
            return (payload && payload.error) || fallback;
        }

        return fallback;
    }
}
